import { jwtDecode } from "jwt-decode";
import { baseUrl } from "./requestUrl";

// The access token lives 30 min. We treat it as "expiring" once we're within
// this buffer of the real expiry, so we refresh quietly ahead of time instead
// of waiting for a guaranteed 401.
const EXPIRY_BUFFER_MS = 2 * 60 * 1000;

let accessToken = localStorage.getItem("accessToken");
// Cached expiry (ms since epoch). Decoded only when the token changes, so the
// per-request check is just a number comparison — no JWT decode in the hot path.
let accessTokenExp = decodeExp(accessToken);

function decodeExp(token) {
  try {
    const { exp } = jwtDecode(token);
    return typeof exp === "number" ? exp * 1000 : 0; // exp is in seconds
  } catch {
    return 0;
  }
}

export const setAccessToken = (token) => {
  accessToken = token;
  accessTokenExp = decodeExp(token);
  if (token) {
    localStorage.setItem("accessToken", token);
  } else {
    localStorage.removeItem("accessToken");
  }
};

// Keep the in-memory token in sync across browser tabs. The `storage` event
// fires in OTHER tabs when localStorage changes, so when one tab refreshes and
// rotates the tokens (or logs out), the others pick up the new access token
// instead of trying to refresh with an already-consumed refresh token.
if (typeof window !== "undefined") {
  window.addEventListener("storage", (event) => {
    // localStorage.clear() (logout) fires with key === null.
    if (event.key === null || event.key === "accessToken") {
      accessToken = localStorage.getItem("accessToken");
      accessTokenExp = decodeExp(accessToken);
    }
  });
}

// True once we're within the buffer of expiry (or the token is missing/unparseable).
const isAccessTokenExpiring = () =>
  !accessToken || accessTokenExp - Date.now() <= EXPIRY_BUFFER_MS;

// Wipe the session and bounce to login. Called ONLY when the refresh token
// itself is rejected (genuinely invalid/expired) — never on a transient
// network or server error, so a blip doesn't log the user out.
const forceLogout = () => {
  localStorage.clear();
  window.location.href = "/user/login";
};

class RefreshError extends Error {
  constructor(message, { fatal }) {
    super(message);
    this.name = "RefreshError";
    this.fatal = fatal; // fatal === refresh token invalid → must log out
  }
}

// Single-flight refresh: while one refresh is in progress, every other caller
// awaits the SAME promise instead of firing its own request. With rotating
// (single-use) refresh tokens, parallel refreshes used to race — the first
// consumed the token and the rest were rejected, logging the user out. This
// collapses that burst into one refresh.
let refreshPromise = null;

const refreshTokens = () => {
  if (!refreshPromise) {
    refreshPromise = doRefresh().finally(() => {
      refreshPromise = null;
    });
  }
  return refreshPromise;
};

const doRefresh = async () => {
  const refreshToken = localStorage.getItem("refreshToken");
  if (!refreshToken) {
    throw new RefreshError("no refresh token", { fatal: true });
  }

  let refreshResponse;
  try {
    refreshResponse = await fetch(`${baseUrl}/auth/refresh_token`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${refreshToken}`,
      },
    });
  } catch (e) {
    // Network failure (offline, DNS, CORS) — transient, keep the session.
    throw new RefreshError("refresh request failed", { fatal: false });
  }

  // Only 401/403 means the refresh token is genuinely no longer valid.
  if (refreshResponse.status === 401 || refreshResponse.status === 403) {
    throw new RefreshError("refresh token rejected", { fatal: true });
  }
  // Any other non-OK (5xx, etc.) is transient — surface it but keep the session.
  if (!refreshResponse.ok) {
    throw new RefreshError("refresh failed", { fatal: false });
  }

  const data = await refreshResponse.json();
  if (!data?.access_token || !data?.refresh_token) {
    throw new RefreshError("malformed refresh response", { fatal: false });
  }

  setAccessToken(data.access_token);
  localStorage.setItem("refreshToken", data.refresh_token);
  return data.access_token;
};

const buildHeaders = (options) => ({
  ...(options.headers || {}),
  Authorization: `Bearer ${accessToken}`,
  "Content-Type": "application/json",
});

export const authFetch = async (url, options = {}, retry = true) => {
  // Proactive refresh: if the access token is at/near expiry, refresh before
  // sending so we don't waste a round-trip on a guaranteed 401.
  if (
    retry &&
    isAccessTokenExpiring() &&
    localStorage.getItem("refreshToken")
  ) {
    try {
      await refreshTokens();
    } catch (err) {
      if (err instanceof RefreshError && err.fatal) {
        forceLogout();
        throw err;
      }
      // Transient failure — fall through and try with the current token; the
      // 401 backstop below gets another chance.
    }
  }

  const response = await fetch(url, {
    ...options,
    cache: "no-store",
    headers: buildHeaders(options),
  });

  if (response.status !== 401 || !retry) return response;

  // Reactive backstop: a 401 slipped through (e.g. client clock skew made the
  // proactive check miss). Refresh once (single-flight) and retry.
  try {
    const newToken = await refreshTokens();
    return await fetch(url, {
      ...options,
      cache: "no-store",
      headers: { ...buildHeaders(options), Authorization: `Bearer ${newToken}` },
    });
  } catch (err) {
    if (err instanceof RefreshError && err.fatal) {
      forceLogout();
    }
    throw err;
  }
};
